import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../store';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks, Layout } from '../constants/theme';
import { getRoleDisplayName } from '../utils/roleBasedAccess';
import { logoutUser } from '../store/authSlice';
import UserRoleSelector from './UserRoleSelector';
import LanguageSwitcher from './LanguageSwitcher';
import { StandardRefreshButton } from './buttons/RefreshButton';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  background: ${Colors.surfaceGlass};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${Colors.borderLight};
  box-shadow: ${Shadows.glass};
  min-height: ${Layout.headerHeightPx};
  height: ${Layout.headerHeightPx};
  z-index: ${Layout.zIndex.header};
  position: sticky;
  top: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg}px;
  flex: 1;
`;

const BreadcrumbContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const ReportTitle = styled.h1`
  font-family: ${FontStacks.display};
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  letter-spacing: -0.025em;
  background: linear-gradient(135deg, ${Colors.textPrimary} 0%, ${Colors.primary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ReportIcon = styled.span`
  font-size: ${FontSizes.lg}px;
`;

const BreadcrumbSeparator = styled.span`
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.normal};
  margin: 0 ${Spacing.md}px;
`;

const WorkspaceName = styled.span`
  font-family: ${FontStacks.display};
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  letter-spacing: -0.025em;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  background: ${Colors.surface};
  backdrop-filter: blur(12px);
  border: 1px solid ${Colors.border};
  color: ${Colors.textSecondary};
  padding: ${Spacing.md}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.lg}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 300ms cubic-bezier(0.4, 0.0, 0.2, 1);
  
  &:hover {
    background: ${Colors.surfaceGlass};
    border-color: ${Colors.primary};
    color: ${Colors.textPrimary};
    transform: translateY(-1px);
    box-shadow: ${Shadows.md};
  }
  
  &.primary {
    background: linear-gradient(135deg, ${Colors.primary} 0%, ${Colors.secondary} 100%);
    border-color: ${Colors.primary};
    color: ${Colors.textOnPrimary};
    box-shadow: ${Shadows.sm};
    
    &:hover {
      background: linear-gradient(135deg, ${Colors.secondary} 0%, ${Colors.primary} 100%);
      border-color: ${Colors.secondary};
      transform: translateY(-2px);
      box-shadow: ${Shadows.lg};
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.xl}px;
  cursor: pointer;
  transition: all 300ms ease;
  background: ${Colors.surfaceGlass};
  backdrop-filter: blur(12px);
  border: 1px solid ${Colors.borderLight};
  
  &:hover {
    background: ${Colors.surfaceHover};
    border-color: ${Colors.primary};
    transform: translateY(-1px);
    box-shadow: ${Shadows.md};
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${BorderRadius.lg}px;
  background: ${Colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textOnPrimary};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border: 2px solid ${Colors.surface};
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UserName = styled.span`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  line-height: 1.2;
`;

const UserRole = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  line-height: 1.2;
`;

const RefreshIndicator = styled.div<{ isRefreshing: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  
  ${props => props.isRefreshing && `
    animation: pulse 1.5s ease-in-out infinite;
  `}
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const StatusDot = styled.div<{ status: 'success' | 'warning' | 'error' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.status) {
      case 'success': return Colors.success;
      case 'warning': return Colors.warning;
      case 'error': return Colors.danger;
      default: return Colors.textTertiary;
    }
  }};
`;

const UserMenuDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${Spacing.sm}px);
  right: 0;
  background: ${Colors.surface};
  backdrop-filter: blur(20px);
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.xl};
  min-width: 240px;
  padding: ${Spacing.md}px;
  z-index: ${Layout.zIndex.dropdown};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 200ms ease;
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  width: 100%;
  padding: ${Spacing.md}px ${Spacing.md}px;
  background: transparent;
  border: none;
  border-radius: ${BorderRadius.sm}px;
  color: ${Colors.textSecondary};
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.normal};
  text-align: left;
  cursor: pointer;
  transition: all 150ms ease;
  position: relative;
  
  &:hover {
    background: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }
  
  &:active {
    background: ${Colors.surfaceSelected};
  }
  
  &.danger {
    color: ${Colors.danger};
    
    &:hover {
      background: rgba(231, 76, 60, 0.08);
      color: ${Colors.danger};
    }
  }
`;

const MenuDivider = styled.hr`
  margin: ${Spacing.md}px 0;
  border: none;
  border-top: 1px solid ${Colors.borderLight};
  opacity: 0.5;
`;

const MenuIcon = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.md}px;
  line-height: 1;
  
  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2;
  }
`;

const MenuLabel = styled.span`
  flex: 1;
  font-size: ${FontSizes.sm}px;
  letter-spacing: -0.01em;
`;

const MenuHeader = styled.div`
  padding: ${Spacing.sm}px ${Spacing.md}px ${Spacing.md}px;
  margin-bottom: ${Spacing.xs}px;
  border-bottom: 1px solid ${Colors.borderLight};
`;

const MenuUserName = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin-bottom: 2px;
`;

const MenuUserRole = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PowerBIHeader: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentRole, userName } = useSelector((state: RootState) => state.user);
  const { lastUpdated } = useSelector((state: RootState) => state.dashboard);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const getPageInfo = () => {
    switch (location.pathname) {
      case '/dashboard':
      case '/':
        return { title: t('header.dashboard'), icon: '' };
      case '/insights':
        return { title: t('header.insights'), icon: '' };
      case '/profiles':
        return { title: t('header.profiles'), icon: '' };
      case '/data-sources':
        return { title: t('header.dataSources'), icon: '' };
      case '/requisitions':
        return { title: 'My Requisitions', icon: '' };
      case '/candidates':
        return { title: 'Candidate Pipeline', icon: '' };
      case '/market-intelligence':
        return { title: 'Market Intelligence', icon: '' };
      case '/admin':
        return { title: 'Administration', icon: '' };
      default:
        if (location.pathname.startsWith('/candidates/')) {
          return { title: 'Candidate Profile', icon: '' };
        }
        if (location.pathname.startsWith('/requisitions/')) {
          return { title: 'Requisition Details', icon: '' };
        }
        return { title: t('header.analytics'), icon: '' };
    }
  };

  const handleRefresh = async (): Promise<void> => {
    // Simulate refresh action
    return new Promise((resolve) => {
      setTimeout(() => {
        window.location.reload();
        resolve();
      }, 500);
    });
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Force navigation to login even if logout fails
      navigate('/login');
    }
  };

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
    setShowRoleSelector(false);
  };

  const handleRoleSelectorToggle = () => {
    setShowRoleSelector(!showRoleSelector);
    setShowUserMenu(false);
  };

  const pageInfo = getPageInfo();

  const formatLastUpdated = (timestamp: string | null) => {
    if (!timestamp) return t('header.neverUpdated');
    const date = new Date(timestamp);
    const timeFormat = i18n.language === 'de' ? 
      date.toLocaleTimeString('de-DE') : 
      date.toLocaleTimeString('en-US');
    return t('header.updated', { time: timeFormat });
  };

  return (
    <>
      <HeaderContainer>
        <HeaderLeft>
          <BreadcrumbContainer>
            <WorkspaceName>{t('header.workspace')}</WorkspaceName>
            <BreadcrumbSeparator>|</BreadcrumbSeparator>
            <ReportTitle>
              {pageInfo.icon && <ReportIcon>{pageInfo.icon}</ReportIcon>}
              {pageInfo.title}
            </ReportTitle>
          </BreadcrumbContainer>
          
          <RefreshIndicator isRefreshing={false}>
            <StatusDot status="success" />
            <span>{formatLastUpdated(lastUpdated)}</span>
          </RefreshIndicator>
        </HeaderLeft>

        <HeaderActions>
          <StandardRefreshButton onClick={handleRefresh} />
          
          <ActionButton>
            {t('header.save')}
          </ActionButton>
          
          <ActionButton>
            {t('header.export')}
          </ActionButton>
          
          <ActionButton className="primary">
            {t('header.edit')}
          </ActionButton>

          <LanguageSwitcher />
          
          <UserMenuContainer ref={userMenuRef}>
            <UserInfo onClick={handleUserMenuToggle}>
              <UserAvatar>
                {userName.split(' ').map((n: string) => n[0]).join('')}
              </UserAvatar>
              <UserDetails>
                <UserName>{userName}</UserName>
                <UserRole>{getRoleDisplayName(currentRole)}</UserRole>
              </UserDetails>
            </UserInfo>
            
            <UserMenuDropdown isOpen={showUserMenu}>
              <MenuHeader>
                <MenuUserName>{userName}</MenuUserName>
                <MenuUserRole>{getRoleDisplayName(currentRole)}</MenuUserRole>
              </MenuHeader>
              <MenuItem onClick={handleRoleSelectorToggle}>
                <MenuIcon>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </MenuIcon>
                <MenuLabel>Switch Role</MenuLabel>
              </MenuItem>
              <MenuItem>
                <MenuIcon>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </MenuIcon>
                <MenuLabel>Settings</MenuLabel>
              </MenuItem>
              <MenuItem>
                <MenuIcon>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M18 20V10m-6 10V4M6 20v-6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </MenuIcon>
                <MenuLabel>My Analytics</MenuLabel>
              </MenuItem>
              <MenuDivider />
              <MenuItem className="danger" onClick={handleLogout}>
                <MenuIcon>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14l5-5-5-5m5 5H9" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </MenuIcon>
                <MenuLabel>Logout</MenuLabel>
              </MenuItem>
            </UserMenuDropdown>
          </UserMenuContainer>
        </HeaderActions>
      </HeaderContainer>

      <UserRoleSelector
        isOpen={showRoleSelector}
        onClose={() => setShowRoleSelector(false)}
      />
    </>
  );
};

export default PowerBIHeader;